import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const SettingsContext = createContext({
  settings: {},
  loading: true,
  refreshSettings: () => {},
  isVisible: (key, defaultVal = true) => defaultVal,
});

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/settings/public`);
      if (res.ok) {
        const data = await res.json();
        setSettings(data || {});
      }
    } catch (err) {
      console.warn('[SettingsContext] Failed to load public settings:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();

    // 1. Multi-tab synchronization via BroadcastChannel
    let channel;
    if (typeof window !== 'undefined' && 'BroadcastChannel' in window) {
      channel = new BroadcastChannel('datx_settings_channel');
      channel.onmessage = (e) => {
        if (e.data === 'SETTINGS_UPDATED') {
          fetchSettings();
        }
      };
    }

    // 2. Custom window event
    const handleSettingsUpdated = () => {
      fetchSettings();
    };
    window.addEventListener('datx-settings-updated', handleSettingsUpdated);

    return () => {
      if (channel) channel.close();
      window.removeEventListener('datx-settings-updated', handleSettingsUpdated);
    };
  }, [fetchSettings]);

  const isVisible = useCallback(
    (key, defaultVal = true) => {
      if (settings[key] === undefined || settings[key] === null) return defaultVal;
      return settings[key] === true || settings[key] === 'true';
    },
    [settings]
  );

  return (
    <SettingsContext.Provider
      value={{
        settings,
        loading,
        refreshSettings: fetchSettings,
        isVisible,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => useContext(SettingsContext);
export default SettingsContext;
